import { useMemo, useState } from 'react'
import { SkuLookupOutcome } from '@/console/api/types'
import { SkuLookupRequest, SkuLookupRun, useSkuLookup } from '@/console/data/devConsoleSkus'
import { Button } from '@/console/ui/Button'
import { Callout } from '@/console/ui/Callout'
import { DataTable, DataTableColumn, DataTableRow } from '@/console/ui/DataTable'
import { Field } from '@/console/ui/Field'
import { Note } from '@/console/ui/Note'
import { ResultState } from '@/console/ui/ResultState'
import { SectionHeading } from '@/console/ui/SectionHeading'
import { SourceBadge } from '@/console/ui/SourceBadge'
import { Stack } from '@/console/ui/Stack'
import { Tag } from '@/console/ui/Tag'
import {
  PastedLines,
  SKU_LOOKUP_AXIS,
  SKU_LOOKUP_MAX_ITEMS,
  SKU_LOOKUP_NEXT_ACTION,
  SKU_LOOKUP_OUTCOMES,
  SKU_LOOKUP_OUTCOME_TONE,
  SkuLookupAxis,
  exceedsLookupLimit,
  formatOutcomeSummary,
  isSubmittableLookup,
  parseOwnerBarcodeLines,
  parseProductOptionLines,
} from './skuLookup'

const AXIS_FORMS = {
  productOption: {
    title: '상품옵션',
    fieldLabel: '한 줄에 platform uid optionCode (platform: MUSINSA · CM29)',
    placeholder: 'MUSINSA 1481065 6106830',
    endpoint: 'by-product-option',
  },
  ownerBarcode: {
    title: '화주+바코드',
    fieldLabel: '한 줄에 shipperCode barcode',
    placeholder: 'INOOINT 32CU252PABN055BK000',
    endpoint: 'by-owner-barcode',
  },
} as const satisfies Record<SkuLookupAxis, { title: string; fieldLabel: string; placeholder: string; endpoint: string }>

type LookupInput = { parsed: PastedLines<unknown>; request: SkuLookupRequest }

const parseLookupInput = (axis: SkuLookupAxis, raw: string): LookupInput => {
  if (axis === SKU_LOOKUP_AXIS.productOption) {
    const parsed = parseProductOptionLines(raw)
    return { parsed, request: { axis, items: parsed.items } }
  }
  const parsed = parseOwnerBarcodeLines(raw)
  return { parsed, request: { axis, items: parsed.items } }
}

const PRODUCT_OPTION_KEY_COLUMNS: DataTableColumn[] = [
  { key: 'platform', label: 'platform' },
  { key: 'uid', label: 'uid', mono: true },
  { key: 'optionCode', label: 'optionCode', mono: true },
]
const OWNER_BARCODE_KEY_COLUMNS: DataTableColumn[] = [
  { key: 'shipperCode', label: 'shipperCode', mono: true },
  { key: 'barcode', label: 'barcode', mono: true },
]
const RESULT_COLUMNS: DataTableColumn[] = [
  { key: 'outcome', label: '판정' },
  { key: 'skuId', label: 'skuId', mono: true },
  { key: 'active', label: '매핑 유효(active)' },
  { key: 'nextAction', label: '다음 행동' },
]

const describeActive = (active: boolean | null | undefined): string => {
  if (active === true) return '유효'
  if (active === false) return '해제됨'
  return '-'
}

type LookupResultFields = { outcome: SkuLookupOutcome; skuId?: string | null; active?: boolean | null }

const resultCells = (result: LookupResultFields): DataTableRow['cells'] => ({
  outcome: <Tag tone={SKU_LOOKUP_OUTCOME_TONE[result.outcome]}>{result.outcome}</Tag>,
  skuId: result.skuId ?? '-',
  active: describeActive(result.active),
  nextAction: SKU_LOOKUP_NEXT_ACTION[result.outcome],
})

const toResultTable = (run: SkuLookupRun): { columns: DataTableColumn[]; rows: DataTableRow[] } => {
  if (run.axis === SKU_LOOKUP_AXIS.productOption) {
    return {
      columns: [...PRODUCT_OPTION_KEY_COLUMNS, ...RESULT_COLUMNS],
      rows: run.response.results.map((result, index) => ({
        key: `${index}:${result.platform}:${result.uid}:${result.optionCode}`,
        cells: { platform: result.platform, uid: result.uid, optionCode: result.optionCode, ...resultCells(result) },
      })),
    }
  }
  return {
    columns: [...OWNER_BARCODE_KEY_COLUMNS, ...RESULT_COLUMNS],
    rows: run.response.results.map((result, index) => ({
      key: `${index}:${result.shipperCode}:${result.barcode}`,
      cells: { shipperCode: result.shipperCode, barcode: result.barcode, ...resultCells(result) },
    })),
  }
}

const AxisSelector = ({ axis, onChange }: { axis: SkuLookupAxis; onChange: (axis: SkuLookupAxis) => void }): JSX.Element => (
  <div role="radiogroup" aria-label="조회 축" style={{ display: 'flex', gap: 16, fontSize: 13.5 }}>
    {(Object.keys(AXIS_FORMS) as SkuLookupAxis[]).map((option) => (
      <label key={option} style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
        <input type="radio" name="sku-lookup-axis" checked={axis === option} onChange={() => onChange(option)} />
        <span>{AXIS_FORMS[option].title}</span>
      </label>
    ))}
  </div>
)

const LookupInputFeedback = ({ parsed }: { parsed: PastedLines<unknown> }): JSX.Element => (
  <>
    <div style={{ fontSize: 12.5, color: 'var(--ink-3)' }}>
      유효 {parsed.items.length}건 · 중복 제외 {parsed.duplicates}건 · 최대 {SKU_LOOKUP_MAX_ITEMS}건
    </div>
    {exceedsLookupLimit(parsed) ? <Callout tone="crit">한 번에 최대 {SKU_LOOKUP_MAX_ITEMS}건까지 조회할 수 있어요.</Callout> : null}
    {parsed.invalidLines.length > 0 ? <Callout tone="warn">형식이 맞지 않는 줄이 있어요: {parsed.invalidLines.join(' / ')}</Callout> : null}
  </>
)

const LookupResult = ({ run }: { run: SkuLookupRun }): JSX.Element => {
  const { columns, rows } = toResultTable(run)
  const outcomes = run.response.results.map((result) => result.outcome)
  return (
    <Stack>
      <div style={{ fontSize: 13.5, fontWeight: 700 }}>{formatOutcomeSummary(outcomes, SKU_LOOKUP_OUTCOMES)}</div>
      <DataTable maxHeight={480} stickyHeader columns={columns} rows={rows} />
    </Stack>
  )
}

const LookupInputField = ({ axis, value, onChange }: { axis: SkuLookupAxis; value: string; onChange: (value: string) => void }): JSX.Element => (
  <Field label={AXIS_FORMS[axis].fieldLabel}>
    <textarea rows={4} spellCheck={false} placeholder={AXIS_FORMS[axis].placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
  </Field>
)

const SkuLookupNote = (): JSX.Element => (
  <Note>
    <b>active</b> 는 SKU 운영상태가 아니라 매핑이 지금도 유효한지예요. 해제된 매핑의 skuId 도 백필할 수 있어요. <b>LOOKUP_FAILED</b> 는 매핑이 없다는 뜻이 아니라 허브 호출 실패예요 — 다시 조회하세요.
  </Note>
)

/** SKU 조회(`s-fopt-sku-lookup`). 조회는 값을 바꾸지 않아 확인 없이 바로 보낸다. */
export const SkuLookupPanel = (): JSX.Element => {
  const [axis, setAxis] = useState<SkuLookupAxis>(SKU_LOOKUP_AXIS.productOption)
  const [rawByAxis, setRawByAxis] = useState<Record<SkuLookupAxis, string>>({ productOption: '', ownerBarcode: '' })
  const { state, lookup } = useSkuLookup()
  const form = AXIS_FORMS[axis]
  const raw = rawByAxis[axis]
  const { parsed, request } = useMemo(() => parseLookupInput(axis, raw), [axis, raw])
  const loading = state.status === 'loading'

  return (
    <section>
      <SectionHeading id="s-fopt-sku-lookup" title="SKU 조회" badge={<SourceBadge tone="new">신규 · POST /dev-console/skus/lookup/{form.endpoint}</SourceBadge>} />
      <Stack>
        <AxisSelector axis={axis} onChange={setAxis} />
        <LookupInputField axis={axis} value={raw} onChange={(value) => setRawByAxis((previous) => ({ ...previous, [axis]: value }))} />
        <LookupInputFeedback parsed={parsed} />
        <div>
          <Button disabled={!isSubmittableLookup(parsed) || loading} onClick={() => lookup(request)}>
            {loading ? '조회 중…' : 'SKU 조회'}
          </Button>
        </div>
        <ResultState state={state}>{state.status === 'ready' ? <LookupResult run={state.data} /> : null}</ResultState>
      </Stack>
      <SkuLookupNote />
    </section>
  )
}
